import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_FILENAME = "invoices-export";

const stringifyValue = (value) => {
  if (value == null) return "";
  if (typeof value === "number") return Number.isFinite(value) ? value.toString() : "";
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "object") {
    if (value.type === "lead") {
      const parts = [value.title, value.subtitle, value.meta].filter(Boolean);
      return parts.join(" · ");
    }
    if (value.type === "status") {
      return value.label || "";
    }
    if (typeof value.label === "string") return value.label;
    if (typeof value.value === "string" || typeof value.value === "number") return String(value.value);
    if (Array.isArray(value)) return value.map((item) => stringifyValue(item)).join(", ");
    return JSON.stringify(value);
  }
  return "";
};

const escapeForCsv = (value) => {
  const raw = stringifyValue(value);
  if (raw.includes(",") || raw.includes("\n") || raw.includes('"')) {
    return `"${raw.replace(/"/g, '""')}"`;
  }
  return raw;
};

const escapeXml = (value) =>
  stringifyValue(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const getColumnConfig = (col = {}) => {
  const accessor = col.getValue || col.exportValue || col.accessor;
  return {
    key: col.key,
    label: col.label || col.key || "",
    getValue: typeof accessor === "function" ? accessor : null,
  };
};

function ExportDropdown({ rows = [], columns = [], exportColumns = [], fileName = DEFAULT_FILENAME }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const disabled = !rows.length;

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [open]);

  const normalizedColumns = useMemo(() => {
    const source = exportColumns?.length ? exportColumns : columns;
    if (source?.length) {
      return source.map((col) => getColumnConfig(col));
    }
    if (!rows.length) return [];
    const sample = rows[0];
    return Object.keys(sample).map((key) => ({ key, label: key, getValue: null }));
  }, [columns, exportColumns, rows]);

  const resolveValue = useCallback(
    (col, row) => {
      if (typeof col.getValue === "function") return col.getValue(row);
      return row[col.key];
    },
    []
  );

  const downloadFile = useCallback((content, extension, mimeType = "text/plain") => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [fileName]);

  const exportCsv = useCallback(() => {
    if (!rows.length) return;
    const headers = normalizedColumns.map((col) => escapeForCsv(col.label || col.key || ""));
    const dataRows = rows.map((row) =>
      normalizedColumns.map((col) => escapeForCsv(resolveValue(col, row))).join(",")
    );
    const csvContent = [headers.join(","), ...dataRows].join("\n");
    downloadFile(csvContent, "csv", "text/csv;charset=utf-8;");
    setOpen(false);
  }, [downloadFile, normalizedColumns, resolveValue, rows]);

  const exportExcel = useCallback(() => {
    if (!rows.length) return;
    const headerRow = normalizedColumns
      .map((col) => `<Cell><Data ss:Type="String">${escapeXml(col.label || col.key || "")}</Data></Cell>`)
      .join("");
    const dataRows = rows
      .map((row) => {
        const cells = normalizedColumns
          .map((col) => {
            const rawValue = stringifyValue(resolveValue(col, row));
            const numericValue = Number(rawValue);
            const isNumber = rawValue !== "" && Number.isFinite(numericValue);
            const data = isNumber ? numericValue : rawValue;
            return `<Cell><Data ss:Type="${isNumber ? "Number" : "String"}">${escapeXml(data)}</Data></Cell>`;
          })
          .join("");
        return `<Row>${cells}</Row>`;
      })
      .join("");
    const worksheet = `<Worksheet ss:Name="Export"><Table><Row>${headerRow}</Row>${dataRows}</Table></Worksheet>`;
    const xml = `<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?>` +
      `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" ` +
      `xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">${worksheet}</Workbook>`;
    downloadFile(xml, "xlsx", "application/vnd.ms-excel");
    setOpen(false);
  }, [downloadFile, normalizedColumns, resolveValue, rows]);

  return (
    <div className="export-dropdown" ref={menuRef}>
      <button
        className="ghost-btn small export-trigger"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={disabled}
      >
        Export ▾
      </button>
      {open ? (
        <div className="dropdown-panel export-menu" role="menu">
          <button className="dropdown-item" type="button" onClick={exportCsv}>
            Export as CSV
          </button>
          <button className="dropdown-item" type="button" onClick={exportExcel}>
            Export as Excel
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default ExportDropdown;
